import React, { useCallback, useMemo, useRef } from 'react';

import { Box, HStack, Icon, Skeleton, VStack } from '@rbilabs/universal-components';
import { randomUUID as uuidv4 } from 'expo-crypto';
import { Image } from 'expo-image';
import { isHydratedReward } from 'features/cart/utils/minimal-cart';
import { ImageBackground, StyleSheet, View } from 'react-native';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { PdpResult, useAppTakeover } from 'components/app-takeover/use-app-takeover';
import { useAppTakeoverCart } from 'components/app-takeover/use-app-takeover-cart';
import { CartDebugMenu } from 'components/debug-menu/cart-debug';
import FavoriteEntryButton from 'components/favorite-entry-button';
import Picture from 'components/picture';
import { useCart } from 'hooks/use-cart';
import { useCartItemEditingTools } from 'hooks/use-cart-item-editing-tools';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import useFoodware from 'hooks/use-foodware';
import { useIsEditCartEnabled } from 'hooks/use-is-edit-cart-enabled';
import { useIsExtraSmallBreakpoint } from 'hooks/use-media-query';
import { ClickEventComponentNames, CustomEventNames, logButtonClick } from 'state/amplitude';
import { useCartContext } from 'state/cart';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { primitive, spacing } from 'styles/constants';
import { getMenuObjectCartQuantity } from 'utils/cart';
import { isCartEntryItem } from 'utils/cart/is-cart-type';
import { CartEntryType } from 'utils/cart/types';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';

import { CartItemOffer } from './cart-item-offer';
import { CartItemPriceAndCalories } from './cart-item-price-and-calories';
import {
  ComposedCartItemType,
  useComposeCartItemUIData,
} from './hooks/use-compose-cart-item-ui-data';
import { useRedeemReward } from './hooks/use-redeem-reward-state';
import { Price } from './price';
import { RedeemReward } from './redeem-reward';
import {
  CartItemButton,
  CartItemButtonText,
  CartItemDescription,
  CartItemEditButtonContainer,
  CartItemEditContainer,
  CartItemHeaderText,
  CartItemInfoContainer,
  CartItemName,
  Item,
} from './styled';
import { ICartItemProps } from './types';

const DISABLED_COLOR = primitive.$blackOpacity50;
const ENABLED_COLOR = primitive.$bbqBrown;

export const CartItem: React.FC<React.PropsWithChildren<ICartItemProps>> = React.memo(
  ({ item, readOnly = false, rewardApplied = false, selectedOfferId, onAnnounceA11yMessage }) => {
    const hasSelectedOffer = !!selectedOfferId;

    const isExtraSmallScreen = useIsExtraSmallBreakpoint();
    const isEditOfferButtonEnabled = useFlag(LaunchDarklyFlag.ENABLE_EDIT_OFFERS_CART_BUTTON);
    const isDebugMenuEnabled = useFlag(LaunchDarklyFlag.ENABLE_DEBUG_TOOL);
    const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
    const isUnifiedMenuEnabled = useEnableUnifiedMenu();

    const isOneMenuComboRewardApplied =
      isUnifiedMenuEnabled && !!rewardApplied && item.type === CartEntryType.combo;

    const dispatch = useAppDispatch();
    const offerSkeletonRenderedRef = useRef(false);

    // TODO: check if we need to evaluate LoyaltyOffer as cartHasIncentive
    const isEditCartEnabled = useIsEditCartEnabled({
      cartHasIncentive: hasSelectedOffer || rewardApplied,
    });

    const { handleEditItem, handleRemoveItem } = useCartItemEditingTools({
      item,
      isOffer: hasSelectedOffer,
    });

    const composedItem = useComposeCartItemUIData(item);

    const itemKey = useMemo(() => item.cartId || item._id, [item._id, item.cartId]);
    const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
    const appliedLoyaltyRewardsArray = useAppSelector(
      selectors.loyalty.selectAppliedLoyaltyRewardsArray
    );
    const loyaltyEnabled = useIsLoyaltyEnabled();
    const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();
    const { addItemToCart, cartEntries } = useCart();
    const isLoadingOffers = useAppSelector(selectors.loyalty.selectOffersLoading);
    const { isOrderPriceLoading } = useCartContext();
    const { indexedMenu } = useUnifiedMenuContext();

    const unifiedMenuEntry = indexedMenu.find(item._id);

    const { maxCartQuantityMet } = getMenuObjectCartQuantity({
      menuObject: item,
      cartEntries,
      unifiedMenuEntry,
    });

    const loyaltyRewardAvailable = getAvailableRewardFromCartEntry(item);
    const { isFoodware } = useFoodware();
    const { hasSufficientBalance, limitPerOrderMet } = useRedeemReward(item);

    const takeoverFontFamily = useAppTakeoverCart()?.fontFamily;
    const itemTakeoverConfig = Object.entries(
      useAppTakeover()?.pdp?.targetMenuItemIds || {}
    ).find(([id]) => item.url.includes(id))?.[1] as PdpResult;

    const loyaltyOffer = appliedOffers.find(lylOffer => lylOffer.cartId === item.cartId);
    const isSingleItem = isCartEntryItem(item) || (isUnifiedMenuEnabled && isHydratedReward(item));
    const itemHasHeader = !isSingleItem || loyaltyOffer?.id || itemTakeoverConfig?.cartItemImage;
    const isLoading = isOrderPriceLoading || isLoadingOffers;
    const showSkeleton = !offerSkeletonRenderedRef.current && isLoading;
    const isExtra = item.isExtra;
    const isItemEditableInline = !hasSelectedOffer && !isExtra && !loyaltyOffer;
    const hasOffer = (hasSelectedOffer && selectedOfferId === item.sanityId) || loyaltyOffer;
    const showFavorite = !hasOffer && !isExtra;
    const showRedeemRewardButton =
      loyaltyEnabled &&
      loyaltyRewardAvailable &&
      isItemEditableInline &&
      !isOneMenuComboRewardApplied;

    const handleEditItemButtonPress = useCallback(() => {
      if (isLoading) {
        return;
      }

      handleEditItem();
    }, [handleEditItem, isLoading]);

    const handleDuplicateItem = useCallback(() => {
      if (isLoading) {
        return;
      }

      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          name: CustomEventNames.BUTTON_CLICK_DUPLICATE_ITEM,
        },
      });

      hapticNotification({ type: HapticsNotificationType.SUCCESS });
      const newCartId = uuidv4();

      const canRedeemReward = hasSufficientBalance && !limitPerOrderMet;
      const currentCartEntryHasRewardApplied = appliedLoyaltyRewardsArray?.some(
        reward => reward.cartId === item.cartId
      );

      addItemToCart(
        { ...item, cartId: newCartId, lineId: newCartId } as ILegacyCartEntry,
        undefined,
        undefined,
        true
      );

      onAnnounceA11yMessage(t('addToCartSuccess', { itemName: item.name }));

      if (
        currentCartEntryHasRewardApplied &&
        canRedeemReward &&
        loyaltyRewardAvailable?.rewardBenefitId &&
        !enableRewardUnstacking
      ) {
        dispatch(
          actions.loyalty.applyReward({
            rewardBenefitId: loyaltyRewardAvailable.rewardBenefitId,
            cartId: newCartId,
            quantity: 1,
            redemptionMethod: RewardRedemptionMethod.MOBILE,
            enableRewardUnstacking: false,
          })
        );
      }
    }, [
      item,
      onAnnounceA11yMessage,
      hasSufficientBalance,
      limitPerOrderMet,
      appliedLoyaltyRewardsArray,
      loyaltyRewardAvailable?.rewardBenefitId,
      addItemToCart,
      dispatch,
      isLoading,
      enableRewardUnstacking,
    ]);

    const onRemoveItemPress = useCallback(() => {
      if (isLoading) {
        return;
      }

      hapticNotification({ type: HapticsNotificationType.WARNING });
      handleRemoveItem();
      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          name: CustomEventNames.BUTTON_CLICK_CART_ITEM_REMOVED,
        },
      });
    }, [handleRemoveItem, isLoading]);

    const ItemDescription = useMemo(() => {
      return ({ composedCartItem }: { composedCartItem: ComposedCartItemType }) => {
        const { name, image } = composedCartItem;

        return (
          <CartItemDescription>
            <VStack paddingRight="$4">
              <Picture size="$14" alt={name} image={image} />
            </VStack>
            <VStack flex={1} justifyContent="center">
              <CartItemName
                {...(!itemHasHeader && { marginRight: spacing.$4 })}
                testID="cart-item-label"
              >
                {name}
              </CartItemName>
              <CartItemPriceAndCalories cartItem={composedCartItem} isSingleItem={isSingleItem} />
            </VStack>
          </CartItemDescription>
        );
      };
    }, [isSingleItem, itemHasHeader]);

    const duplicateButton = useMemo(() => {
      if (isOneMenuComboRewardApplied) {
        return null;
      }

      return (
        <CartItemButton
          onPress={handleDuplicateItem}
          testID="duplicate-button"
          keepIconSize
          accessibilityLabel={t('duplicate', { itemName: item.name })}
          leftIcon={<Icon name="custom:DuplicateItem" size="md" />}
          marginRight="$4"
          disabled={maxCartQuantityMet || isLoading}
          color={maxCartQuantityMet || isLoading ? DISABLED_COLOR : ENABLED_COLOR}
        />
      );
    }, [
      isOneMenuComboRewardApplied,
      handleDuplicateItem,
      item.name,
      maxCartQuantityMet,
      isLoading,
    ]);

    // Render the header for offers or non single items.
    const header = useMemo(() => {
      if (loyaltyOffer?.id) {
        return (
          <CartItemOffer
            isLoading={showSkeleton}
            loyaltyOfferId={loyaltyOffer.id}
            cmsOfferId={loyaltyOffer.cmsId}
            skeletonRenderedRef={offerSkeletonRenderedRef}
          />
        );
      }

      const Container = itemTakeoverConfig?.cartItemBackgroundImage ? ImageBackground : View;

      return (
        (!isSingleItem || itemTakeoverConfig?.cartItemImage) && (
          <Container
            style={styles.header}
            source={itemTakeoverConfig?.cartItemBackgroundImage}
            imageStyle={{
              borderTopLeftRadius: primitive.$borderRadius,
              borderTopRightRadius: primitive.$borderRadius,
            }}
          >
            {itemTakeoverConfig?.cartItemImage && (
              <Image
                source={itemTakeoverConfig?.cartItemImage}
                style={{ width: 50, marginLeft: spacing.$3 }}
              />
            )}
            <CartItemHeaderText fontFamily={takeoverFontFamily} testID="cart-item-label">
              {item.name}
            </CartItemHeaderText>
          </Container>
        )
      );
    }, [
      loyaltyOffer?.id,
      loyaltyOffer?.cmsId,
      isSingleItem,
      item.name,
      showSkeleton,
      itemTakeoverConfig?.cartItemImage,
      itemTakeoverConfig?.cartItemBackgroundImage,
      takeoverFontFamily,
    ]);

    if (item.isDonation || isFoodware(item)) {
      return null;
    }

    /**
     * The whole order discounts should not show in the list of cart items.
     * The details for this is explicitly shown outside of the cart item list.
     * The offer must be applied to the cart items, so we're simply removing it
     * visually from the list here.
     */
    if (loyaltyOffer?.cartId === DISCOUNT_OFFER_CART_ID) {
      return null;
    }

    const favoriteAbsoluteButton = showFavorite && (
      <Box position="absolute" right={0} zIndex={1}>
        <FavoriteEntryButton cartEntry={item} disabled={isLoading} />
      </Box>
    );

    if (!composedItem) {
      return null;
    }

    return (
      <Item testID={`cart-item-${itemKey}`}>
        {favoriteAbsoluteButton}
        {itemHasHeader && header}
        <CartItemInfoContainer>
          {isDebugMenuEnabled && <CartDebugMenu cartEntry={item} />}
          {composedItem.children?.length ? (
            composedItem.children.map((composedCartItem, key) => {
              return <ItemDescription key={key} composedCartItem={composedCartItem} />;
            })
          ) : (
            <ItemDescription composedCartItem={composedItem} />
          )}
        </CartItemInfoContainer>

        {!readOnly && (
          <>
            <CartItemEditContainer>
              <CartItemEditButtonContainer marginRight="$4">
                {hasOffer && showSkeleton ? (
                  <Skeleton height="$7" rounded="8" width={'$17_5'} />
                ) : (
                  <>
                    <CartItemButton
                      keepIconSize
                      accessibilityLabel={`${t('remove')} ${item.name} `}
                      testID="remove-button"
                      onPress={onRemoveItemPress}
                      leftIcon={<Icon variant="bkRemove" size="md" />}
                      marginRight="$4"
                      disabled={isLoading}
                      color={isLoading ? DISABLED_COLOR : ENABLED_COLOR}
                    />
                    {!loyaltyOffer?.id && !isExtra && duplicateButton}
                    {isEditCartEnabled && (loyaltyOffer ? isEditOfferButtonEnabled : true) && (
                      <CartItemButton
                        accessibilityLabel={`${t('edit')} ${item.name} `}
                        testID="edit-button"
                        onPress={handleEditItemButtonPress}
                        disabled={isLoading}
                      >
                        <CartItemButtonText color={isLoading ? DISABLED_COLOR : ENABLED_COLOR}>
                          {t('edit')}
                        </CartItemButtonText>
                      </CartItemButton>
                    )}
                  </>
                )}
              </CartItemEditButtonContainer>
              {hasOffer && showSkeleton ? (
                <Skeleton height="$7" rounded="12" width="$20" />
              ) : (
                <CartItemEditButtonContainer justifyContent="flex-end">
                  {showRedeemRewardButton && (
                    <RedeemReward
                      isRewardApplied={rewardApplied}
                      item={item}
                      reward={loyaltyRewardAvailable}
                      disabled={isLoading}
                    />
                  )}
                  {(!showRedeemRewardButton || (showRedeemRewardButton && !isExtraSmallScreen)) && (
                    <HStack marginLeft="$2">
                      <Price item={item} isOffer={hasSelectedOffer} rewardApplied={rewardApplied} />
                    </HStack>
                  )}
                </CartItemEditButtonContainer>
              )}
            </CartItemEditContainer>
            {showRedeemRewardButton && isExtraSmallScreen && (
              <HStack paddingX="$4" paddingY={'$2'} justifyContent="flex-end">
                <Price item={item} isOffer={hasSelectedOffer} rewardApplied={rewardApplied} />
              </HStack>
            )}
          </>
        )}
      </Item>
    );
  }
);

const styles = StyleSheet.create({
  header: {
    borderBottomWidth: 1,
    borderBottomColor: primitive.$blackOpacity10,
    borderTopLeftRadius: primitive.$borderRadius,
    borderTopRightRadius: primitive.$borderRadius,
    flexDirection: 'row',
    overflow: 'hidden',
  },
});
